// TODO: Fix the circular dependency issue
import type { AppViews } from '@src/config';
import type { Orientation } from '@src/store/dicom-web/classification-typing';

import type { ImageIndices } from '../dicom-web/current-study-store';
import type vtkLPSView2DProxy from '@/src/vtk/LPSView2DProxy';

import {
  getNeighborViewIDs,
  InitViewIDs,
  isOrientationSupported,
  RestrictedContourInteractionViewIDs,
  RestrictToBreathholdViewIDs,
  // checkComponentViewIDPermission,
  shouldShowTool,
} from '@src/config';
import { ViewProxyType } from '@src/core/proxies';

import { BiomarkerType } from '@src/store/analysis/data-typing';
import { ContourLabel, encodeContourLabelItemId } from '@src/store/contours/contour-definitions';

import { seriesClassification } from '@src/store/dicom-web/SeriesClassification';
import { defineStore } from 'pinia';

import { useAlert } from '@/src/composables/useAlert';
import { useViewProxy } from '@/src/composables/useViewProxy';

import { useActiveStudyStore } from '@/src/store/active-study';

import {
  areImageIndicesEqual,
  useCurrentStudyStore,
} from '../dicom-web/current-study-store';

import * as ContourDefinitions from './contour-definitions';
import { ViewportDrawingMode, ViewportToolMode } from './types/tool-modes-type';
import { getSeriesRuleViolations } from './viewport-series-rules';

export const DrawingModeToCursor: Record<ViewportDrawingMode, string> = {
  [ViewportDrawingMode.Freehand]: '/icons/image-interaction/default.svg',
  [ViewportDrawingMode.PointClick]: '/icons/image-interaction/default.svg',
  [ViewportDrawingMode.Threshold]: '/icons/image-interaction/default.svg',
  [ViewportDrawingMode.Nudge]: '/icons/image-interaction/default.svg',
  [ViewportDrawingMode.None]: '/icons/image-interaction/default.svg',
};

export const ToolModeToCursor: Record<ViewportToolMode, string> = {
  [ViewportToolMode.WindowLevel]: `url('/icons/image-interaction/windowing_combined.svg')`,
  [ViewportToolMode.Pan]: `url('/icons/image-interaction/default.svg')`,
  [ViewportToolMode.Zoom]: `url('/icons/image-interaction/zooming_combined.svg')`,
  [ViewportToolMode.ZoomSide]: `url('/icons/image-interaction/zooming_combined.svg')`,
  [ViewportToolMode.Rotate]: `url('/icons/image-interaction/rotation_combined.svg')`,
  [ViewportToolMode.Contour]: '', // Special handling below
  [ViewportToolMode.None]: `url('/icons/image-interaction/default.svg')`,
};

async function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    // Failed loads must not hang forever — that left the cursor stuck/unset
    // when ContourLabelToMiniIcon had no entry for the active label.
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function combineIcons(toolUrl: string, badgeUrl?: string | null) {
  const toolImg = await loadImage(toolUrl);
  if (!toolImg) return null;

  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  if (badgeUrl) {
    const badgeImg = await loadImage(badgeUrl);
    if (badgeImg) {
      ctx.drawImage(badgeImg, 0, 0, 32, 32);
    }
  }
  ctx.drawImage(toolImg, 0, 0, 32, 32);

  return canvas.toDataURL('image/png');
}

// Per-viewport generation token so a slower async cursor build cannot overwrite
// a newer one (e.g. rapid tool/label switches while icons are still loading).
const cursorUpdateTokens = new Map<string, number>();

export interface ViewportState {
  selectedContour: {
    label: ContourDefinitions.ContourLabel | null;
    id: string | null;
  };
  selectedMaskType: string | null;
  selectedMasks: {
    [key: string]: {
      markerType: string | null;
      maskType: BiomarkerType | null;
      colormap?: string;
    };
  };
  moveContour: {
    label: ContourDefinitions.ContourLabel | null;
    id: string | null;
    distancePoint: [number, number];
  };
  hideDrawingContour: {
    label: ContourDefinitions.ContourLabel | null;
    id: string | null;
  };
  hideDrawingContourLabel: ContourDefinitions.ContourLabel | null;
  drawingContour: ContourDefinitions.ContourLabel | null;
  drawingMode: ViewportDrawingMode;
  drawingActive: boolean;
  toolMode: ViewportToolMode;
  showGrid: boolean;
  showContours: boolean;
  showMasks: boolean;
  showOverlayImages: boolean;
  useColorLut: boolean;
  showAnnotations: boolean;
  showLocalizerOutline: boolean;
  imageInterpolationMode: 'nearest' | 'linear' | 'cubic';
  // reference image uids
  referenceImageUids: string[];
  activeReferenceImageUid: string | null;

  // selected image indices
  selectedImageIndices: ImageIndices;
  // lastImageIndicesUpdateTime: number;
  saxLvTransmuralityOffsetEndo: number;
  saxLvTransmuralityOffsetEpi: number;

  // generated contours
  visibleGeneratedContours: ContourDefinitions.ContourLabel[];
  visibleGeneratedContourIds: ContourDefinitions.ContourId[];
  hiddenUserEditableContours: ContourDefinitions.ContourLabel[];

  // Restricts which contour labels may be drawn in this view (e.g. the pie
  // menu). `null` means no restriction (all contour labels are available).
  allowedContourLabels: ContourDefinitions.ContourLabel[] | null;
}

const SYNC_VIEWERS = true;

function getDefaultColormap(): string {
  return 'plasma';
}

function getDefaultViewportState(): ViewportState {
  return {
    selectedContour: {
      label: null,
      id: null,
    },
    selectedMaskType: null,
    selectedMasks: {},
    moveContour: {
      label: null,
      id: null,
      distancePoint: [0, 0],
    },
    hideDrawingContour: {
      label: null,
      id: null,
    },
    hideDrawingContourLabel: null,
    drawingContour: null,
    drawingMode: ViewportDrawingMode.Freehand,
    drawingActive: false,
    toolMode: ViewportToolMode.Pan,
    showGrid: false,
    showContours: true,
    showMasks: true,
    showOverlayImages: true,
    useColorLut: true,
    showAnnotations: true,
    imageInterpolationMode: 'cubic',
    showLocalizerOutline: false,

    // reference image uids
    referenceImageUids: [],
    activeReferenceImageUid: null,

    selectedImageIndices: {
      seriesIdx: -1,
      sliceIdx: -1,
      imageIdx: -1,
    },
    // lastImageIndicesUpdateTime: 0,
    saxLvTransmuralityOffsetEndo: 15,
    saxLvTransmuralityOffsetEpi: 10,

    visibleGeneratedContours: [],
    visibleGeneratedContourIds: [],
    hiddenUserEditableContours: [],

    allowedContourLabels: null,
  };
}

export const useViewportStateStore = defineStore('viewportStateStore', {
  state: (): {
    viewportStates: Record<string, ViewportState>;
    globalOffsets: {
      saxLvTransmuralityOffsetEndo: number | null;
      saxLvTransmuralityOffsetEpi: number | null;
    };
  } => ({
    viewportStates: {},
    // imageIndicesThrottleDelay: 50, // milliseconds
    globalOffsets: {
      saxLvTransmuralityOffsetEndo: 15,
      saxLvTransmuralityOffsetEpi: 10,
    },
  }),
  actions: {
    initViewportState(viewportId: string) {
      if (!this.viewportStates[viewportId]) {
        this.viewportStates[viewportId] = getDefaultViewportState();

        // Check if global offsets are set, if so initialize the viewport state with those values
        if (this.globalOffsets.saxLvTransmuralityOffsetEndo !== null) {
          this.viewportStates[viewportId].saxLvTransmuralityOffsetEndo
            = this.globalOffsets.saxLvTransmuralityOffsetEndo!;
        }
        if (this.globalOffsets.saxLvTransmuralityOffsetEpi !== null) {
          this.viewportStates[viewportId].saxLvTransmuralityOffsetEpi
            = this.globalOffsets.saxLvTransmuralityOffsetEpi!;
        }

        // Set SAXLvTransmuralityOffsets to active for needed viewports:
        const validLVTransmuralityViewIDs = [InitViewIDs.ContoursLeft, InitViewIDs.ContoursRight];
        if (
          validLVTransmuralityViewIDs.includes(viewportId)
          && this.globalOffsets.saxLvTransmuralityOffsetEndo !== null
          && this.globalOffsets.saxLvTransmuralityOffsetEpi !== null
        ) {
          const endoOffset = this.globalOffsets.saxLvTransmuralityOffsetEndo;
          const epiOffset = this.globalOffsets.saxLvTransmuralityOffsetEpi;
          this.viewportStates[viewportId].visibleGeneratedContourIds = [
            { label: ContourLabel.SaxLvTransmurality, id: encodeContourLabelItemId([endoOffset]) },
            { label: ContourLabel.SaxLvTransmurality, id: encodeContourLabelItemId([100 - epiOffset]) },
          ];
        }
      }
    },
    clearViewportState(viewportId: string) {
      this.viewportStates[viewportId] = getDefaultViewportState();
    },
    setSelectedMarker(
      viewportId: string,
      markerType: string,
      maskType: BiomarkerType,
    ) {
      this.initViewportState(viewportId);
      const existing
        = this.viewportStates[viewportId].selectedMasks[markerType];
      const colormap = existing?.colormap || getDefaultColormap();

      this.viewportStates[viewportId].selectedMasks[markerType] = {
        markerType,
        maskType,
        colormap,
      };
      this.viewportStates[viewportId].selectedMaskType = markerType;
    },
    setSelectedColormap(viewportId: string, colormap: string) {
      this.initViewportState(viewportId);
      const selectedMaskType = this.getSelectedMaskType(viewportId);
      if (
        selectedMaskType
        && this.viewportStates[viewportId].selectedMasks[selectedMaskType]
      ) {
        this.viewportStates[viewportId].selectedMasks[
          selectedMaskType
        ].colormap = colormap;
      }
    },
    getSelectedColormap(viewportId: string): string {
      this.initViewportState(viewportId);
      const selectedMaskType = this.getSelectedMaskType(viewportId);
      if (selectedMaskType) {
        const maskInfo
          = this.viewportStates[viewportId].selectedMasks[selectedMaskType];
        if (maskInfo) {
          return maskInfo.colormap || getDefaultColormap();
        }
      }
      return 'plasma';
    },
    getSelectedMaskType(viewportId: string): string | null {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].selectedMaskType;
    },
    getSelectedMask(viewportId: string, markerType: string) {
      this.initViewportState(viewportId);
      return (
        this.viewportStates[viewportId].selectedMasks[markerType]?.maskType
        || null
      );
    },
    getSelectedMaskByViewport(viewportId: string): BiomarkerType | null {
      this.initViewportState(viewportId);
      const selectedMaskType = this.getSelectedMaskType(viewportId) || null;
      if (!selectedMaskType) return null;
      return (
        this.viewportStates[viewportId].selectedMasks[selectedMaskType]
          ?.maskType || null
      );
    },
    getSelectedDynamicMarker(viewportId: string) {
      return (
        this.viewportStates[viewportId].selectedMasks.Dynamic?.maskType || null
      );
    },
    getSelectedStaticMask(viewportId: string) {
      return (
        this.viewportStates[viewportId].selectedMasks.Static?.maskType || null
      );
    },
    // selected image
    setSelectedImageIndices(
      viewportId: string,
      indices: ImageIndices,
      ignoreChecks = false,
    ) {
      const viewportIdToUse = viewportId;
      this.initViewportState(viewportIdToUse);

      // // Throttle to prevent spam (per-viewport)
      // const now = Date.now();
      // if (
      //   now - this.viewportStates[viewportIdToUse].lastImageIndicesUpdateTime <
      //   this.imageIndicesThrottleDelay
      // ) {
      //   return;
      // }
      // this.viewportStates[viewportIdToUse].lastImageIndicesUpdateTime = now;

      const currentIndices = this.getSelectedImageIndices(viewportIdToUse);
      // const sliceChanged
      //   = indices.sliceIdx !== currentIndices.sliceIdx
      //     || indices.seriesIdx !== currentIndices.seriesIdx;

      // guard against circular updates
      if (
        !areImageIndicesEqual(
          this.viewportStates[viewportIdToUse].selectedImageIndices,
          indices,
        )
      ) {
        if (ignoreChecks) {
          this.viewportStates[viewportIdToUse].selectedImageIndices = indices;
          useCurrentStudyStore().syncQueueWithViewports(viewportIdToUse);
          return;
        }

        // Validate the series against this viewport's rules, but only when the
        // series actually changes (scrubbing slices/phases within an already
        // validated series must not be re-checked). A failing rule hard-rejects
        // the selection so the previous series is kept.
        if (indices.seriesIdx !== currentIndices.seriesIdx) {
          const violations = getSeriesRuleViolations(
            viewportId,
            indices.seriesIdx,
          );
          if (violations.length > 0) {
            useAlert().warning(
              `Series not valid for ${viewportId}: ${violations
                .map(v => v.reason)
                .filter(Boolean)
                .join(' ')}`,
            );
            return;
          }
        }

        if (viewportId === 'FlowMagnitude' || viewportId === 'FlowPhase') {
          const firstImageIndices = { ...indices, sliceIdx: 0, imageIdx: 0 };
          const imageUID
            = useCurrentStudyStore().getImageUID(firstImageIndices);
          const imageDicomInfo
            = useCurrentStudyStore().imageInfoDicom?.[imageUID];
          if (imageDicomInfo?.IsVencImage2D) {
            const magnitudeImageIndices = { ...indices, sliceIdx: 1 };
            const phaseImageIndices = { ...indices, sliceIdx: 0 };
            this.viewportStates.FlowMagnitude.selectedImageIndices
              = magnitudeImageIndices;
            this.viewportStates.FlowPhase.selectedImageIndices
              = phaseImageIndices;
            useCurrentStudyStore().syncQueueWithViewports(viewportIdToUse);
            return;
          }
        }

        // Check if the series index is changing:
        // if (currentIndices.seriesIdx !== indices.seriesIdx) {
        //   // Check whether user is allowed to change series in this viewport
        //   // Usefule for analysis views where changing series is not allowed
        //   const canChange = checkComponentViewIDPermission(
        //     'ChangeSeries',
        //     viewportId
        //   );

        //   if (!canChange) {
        //     useAlert().warning(`Changing series not allowed in ${viewportId}.`);
        //     return;
        //   }
        // }

        // const classification =
        //   seriesClassification.getClassificationByViewport(viewportId);

        // if (!classification) {
        //   this.viewportStates[viewportIdToUse].selectedImageIndices = indices;
        //   if (sliceChanged)
        //     useCurrentStudyStore().addSliceToBackgroundQueue(indices);
        //   return;
        // }

        // const orientationSupported = this.isOrientationSupportedByViewID(
        //   viewportIdToUse,
        //   classification?.orientation
        // );
        // if (!orientationSupported) {
        //   useAlert().warning(
        //     `Orientation ${classification?.orientation} not supported in ${viewportIdToUse}.`
        //   );
        //   return;
        // }
        this.viewportStates[viewportIdToUse].selectedImageIndices = indices;
        useCurrentStudyStore().syncQueueWithViewports(viewportIdToUse);
      }
    },
    getSelectedImageIndices(viewportId: string): ImageIndices {
      const viewportIdToUse = viewportId;

      this.initViewportState(viewportIdToUse);
      return this.viewportStates[viewportIdToUse].selectedImageIndices;
    },
    serializeSelectedImageIndices(): Record<string, ImageIndices> {
      const serialized: Record<string, ImageIndices> = {};
      Object.keys(this.viewportStates).forEach((viewportId) => {
        serialized[viewportId] = this.getSelectedImageIndices(viewportId);
      });
      return serialized;
    },
    deserializeSelectedImageIndices(serialized: Record<string, ImageIndices>) {
      if (!serialized) return;
      // Go through each viewport in the serialized data
      Object.keys(serialized).forEach((viewportId) => {
        const indices = serialized[viewportId];
        this.setSelectedImageIndices(viewportId, indices, true); // ignoreChecks = true to avoid circular updates
      });
    },
    getSelectedWlId(viewportId: string): string {
      const indices = this.getSelectedImageIndices(viewportId);
      const imageUID = this.getSelectedImageUID(viewportId);
      const imageDicomInfo = useCurrentStudyStore().imageInfoDicom?.[imageUID];
      return `${indices.seriesIdx.toString()}-${imageDicomInfo?.ImageType ?? ''
      }`;
    },
    getSelectedImageUID(viewportId: string): string {
      this.initViewportState(viewportId);
      const indices = this.getSelectedImageIndices(viewportId);
      const imageUID = useCurrentStudyStore().getImageUID(indices);
      return imageUID;
    },
    getSelectedSeriesUID(viewportId: string): string {
      this.initViewportState(viewportId);
      const indices = this.getSelectedImageIndices(viewportId);
      const seriesUID = useCurrentStudyStore().getSeriesUID(indices.seriesIdx);
      return seriesUID;
    },

    isOrientationSupportedByViewID(
      viewID: string,
      orientation: Orientation,
    ): boolean {
      return isOrientationSupported(viewID, orientation);
    },

    shouldShowToolByAppView(toolName: string, appView: AppViews): boolean {
      return shouldShowTool(toolName, appView);
    },

    // Update the selected contour for a viewport
    setSelectedContour(
      viewportId: string,
      label: ContourDefinitions.ContourLabel | null,
      id: string | null,
    ) {
      this.initViewportState(viewportId);
      // Extra check to prevent drawing on restricted viewports
      if (RestrictedContourInteractionViewIDs.includes(viewportId)) return;
      this.viewportStates[viewportId].selectedContour = { label, id };
    },
    getSelectedContour(
      viewportId: string,
    ): ContourDefinitions.ContourId | null {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId]
        .selectedContour as ContourDefinitions.ContourId;
    },

    setMoveContour(
      viewportId: string,
      label: ContourDefinitions.ContourLabel | null,
      id: string,
      distancePoint: [number, number],
    ) {
      this.initViewportState(viewportId);
      // Extra check to prevent drawing on restricted viewports
      if (RestrictedContourInteractionViewIDs.includes(viewportId)) return;
      this.viewportStates[viewportId].moveContour = {
        label,
        id,
        distancePoint,
      };
    },
    getMoveContour(viewportId: string): ViewportState['moveContour'] | null {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].moveContour;
    },
    setHideDrawingContour(
      viewportId: string,
      label: ContourDefinitions.ContourLabel | null,
      id: string,
    ) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].hideDrawingContour = { label, id };
    },
    getHideDrawingContour(
      viewportId: string,
    ): ViewportState['hideDrawingContour'] | null {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].hideDrawingContour;
    },
    setHideDrawingContourLabel(
      viewportId: string,
      label: ContourDefinitions.ContourLabel | null,
    ) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].hideDrawingContourLabel = label;
    },
    getHideDrawingContourLabel(
      viewportId: string,
    ): ContourDefinitions.ContourLabel | null {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].hideDrawingContourLabel;
    },

    // Update the drawing contour for a viewport
    setDrawingContour(
      viewportId: string,
      label: ContourDefinitions.ContourLabel | null,
    ) {
      this.initViewportState(viewportId);
      // Extra check to prevent drawing on restricted viewports
      if (label && RestrictedContourInteractionViewIDs.includes(viewportId))
        return;
      this.viewportStates[viewportId].drawingContour = label;
      if (label) {
        this.setShowContours(viewportId, true);
      }
      void this.updateViewportCursor(viewportId);
      if (SYNC_VIEWERS) {
        const allViewIDs = getNeighborViewIDs(viewportId);
        allViewIDs.forEach((id) => {
          if (label && RestrictedContourInteractionViewIDs.includes(viewportId))
            return;
          this.initViewportState(id);
          this.viewportStates[id].drawingContour = label;
          void this.updateViewportCursor(id);
        });
      }
    },
    getDrawingContour(
      viewportId: string,
    ): ContourDefinitions.ContourLabel | null {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].drawingContour;
    },
    // for convenience
    getDrawingContourProps(
      viewportId: string,
    ): ContourDefinitions.ContourProps | null {
      this.initViewportState(viewportId);
      const contourLabel = this.viewportStates[viewportId].drawingContour;
      if (!contourLabel) return null;
      return ContourDefinitions.ContourDefinitions[contourLabel];
    },
    getDrawingContourCursorIconUrl(viewportId: string): string {
      this.initViewportState(viewportId);
      const contourProps = this.getDrawingContourProps(viewportId);
      if (!contourProps) return '';
      return contourProps.icon;
    },

    // Restrict which contour labels may be drawn in this view. Pass `null` to
    // remove any restriction (all contour labels become available again).
    setAllowedContourLabels(
      viewportId: string,
      labels: ContourDefinitions.ContourLabel[] | null,
    ) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].allowedContourLabels = labels;
    },
    getAllowedContourLabels(
      viewportId: string,
    ): ContourDefinitions.ContourLabel[] | null {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].allowedContourLabels;
    },
    isContourLabelAllowed(
      viewportId: string,
      label: ContourDefinitions.ContourLabel,
    ): boolean {
      this.initViewportState(viewportId);
      const allowed = this.viewportStates[viewportId].allowedContourLabels;
      return !allowed || allowed.includes(label);
    },

    // Update the drawing mode for a viewport
    setDrawingMode(viewportId: string, mode: ViewportDrawingMode) {
      this.initViewportState(viewportId);
      // Extra check to prevent drawing on restricted viewports
      if (RestrictedContourInteractionViewIDs.includes(viewportId)) return;
      this.viewportStates[viewportId].drawingMode = mode;
      if (mode !== ViewportDrawingMode.None) {
        this.setShowContours(viewportId, true);
      }
      void this.updateViewportCursor(viewportId);

      if (SYNC_VIEWERS) {
        const allViewIDs = getNeighborViewIDs(viewportId);
        allViewIDs.forEach((id) => {
          this.viewportStates[id].drawingMode = mode;
          void this.updateViewportCursor(id);
        });
      }
    },
    getDrawingMode(viewportId: string): ViewportDrawingMode {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].drawingMode;
    },

    // Activate or deactivate drawing for a viewport
    setDrawingActive(viewportId: string, active: boolean) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].drawingActive = active;
    },
    getDrawingActive(viewportId: string): boolean {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].drawingActive;
    },

    async updateViewportCursor(viewportId: string) {
      this.initViewportState(viewportId);

      const token = (cursorUpdateTokens.get(viewportId) ?? 0) + 1;
      cursorUpdateTokens.set(viewportId, token);

      const { viewProxy } = useViewProxy<vtkLPSView2DProxy>(
        viewportId,
        ViewProxyType.Slice,
      );
      const interactor = viewProxy.value.getRenderWindow().getInteractor();
      const container = interactor.getContainer();
      if (!container) {
        return;
      }
      if (viewProxy.value) {
        // Get the current toolMode, drawingMode, and drawingContour:
        const { toolMode, drawingMode, drawingContour }
          = this.viewportStates[viewportId];
        let cursor: string | null = 'default';
        const hotspotX = 0;
        const hotspotY = 0;

        // 1. Check whether toolmode is contour:
        if (toolMode === ViewportToolMode.Contour && drawingContour) {
          const drawingModeIcon = DrawingModeToCursor[drawingMode];
          // Missing mini icons previously fell back to the string 'default',
          // which was passed to Image() and never loaded — hanging the await
          // and leaving the previous/default cursor stuck.
          const contourIcon
            = ContourDefinitions.ContourLabelToMiniIcon[drawingContour] ?? null;
          // ToDo cache cursor icon?
          const combined = await combineIcons(drawingModeIcon, contourIcon);
          // Drop stale results from an older in-flight update.
          if (cursorUpdateTokens.get(viewportId) !== token) return;
          cursor = combined
            ? `url('${combined}')`
            : `url('${drawingModeIcon}')`;
        }
        else {
          // 2. Otherwise, use the toolmode cursor:
          cursor = ToolModeToCursor[toolMode] || 'default';
        }
        if (cursorUpdateTokens.get(viewportId) !== token) return;
        container.style.cursor = `${cursor} ${hotspotX} ${hotspotY}, auto`;
      }
    },

    // Update the tool mode for a viewport
    setToolMode(viewportId: string, mode: ViewportToolMode) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].toolMode = mode;
      if (mode === ViewportToolMode.Contour) {
        this.setShowContours(viewportId, true);
      }

      void this.updateViewportCursor(viewportId);

      if (SYNC_VIEWERS) {
        const allViewIDs = getNeighborViewIDs(viewportId);

        allViewIDs.forEach((id) => {
          // In cases where the viewID has not been init (flow, function) skip
          const viewState = this.viewportStates[id];

          if (!viewState) return;

          viewState.toolMode = mode;
          void this.updateViewportCursor(id);
        });
      }
    },
    getToolMode(viewportId: string): ViewportToolMode {
      this.initViewportState(viewportId);

      return this.viewportStates[viewportId].toolMode;
    },

    setShowContours(viewportId: string, show: boolean) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].showContours = show;
    },
    getShowContours(viewportId: string): boolean {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].showContours;
    },
    setShowMasks(viewportId: string, show: boolean) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].showMasks = show;
    },
    getShowMasks(viewportId: string): boolean {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].showMasks;
    },
    setShowOverlayImages(viewportId: string, show: boolean) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].showOverlayImages = show;
    },
    getShowOverlayImages(viewportId: string): boolean {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].showOverlayImages;
    },

    setUseColorLut(viewportId: string, use: boolean) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].useColorLut = use;
    },
    getUseColorLut(viewportId: string): boolean {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].useColorLut;
    },

    setShowAnnotations(viewportId: string, show: boolean) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].showAnnotations = show;
    },
    getShowAnnotations(viewportId: string): boolean {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].showAnnotations;
    },

    setImageInterpolationMode(
      viewportId: string,
      mode: 'nearest' | 'linear' | 'cubic',
    ) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].imageInterpolationMode = mode;
    },
    getImageInterpolationMode(
      viewportId: string,
    ): 'nearest' | 'linear' | 'cubic' {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].imageInterpolationMode;
    },

    // reference image uids
    addReferenceImage(
      viewportId: string,
      uid: string,
      replace: boolean = true,
    ) {
      this.initViewportState(viewportId);
      if (replace) {
        this.viewportStates[viewportId].referenceImageUids = [uid];
      }
      else {
        this.viewportStates[viewportId].referenceImageUids.push(uid);
      }
    },
    getReferenceImageUids(viewportId: string): string[] {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].referenceImageUids;
    },
    clearReferenceImageUids(viewportId: string) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].referenceImageUids = [];
    },
    setActiveReferenceImageUid(viewportId: string, uid: string) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].activeReferenceImageUid = uid;
    },
    getActiveReferenceImageUid(viewportId: string): string | null {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].activeReferenceImageUid;
    },
    setShowLocalizerOutline(viewportId: string, show: boolean) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].showLocalizerOutline = show;
    },
    getShowLocalizerOutline(viewportId: string): boolean {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].showLocalizerOutline;
    },

    // generated contour display
    setSaxLvTransmuralityOffsetEndo(viewportId: string, offset: number) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].saxLvTransmuralityOffsetEndo = offset;
    },
    setSaxLvTransmuralityOffsetsGlobal(epiOffset: number, endoOffset: number) {
      // First initialize globalOffsets (to be used by all viewports)
      this.globalOffsets.saxLvTransmuralityOffsetEndo = endoOffset;
      this.globalOffsets.saxLvTransmuralityOffsetEpi = epiOffset;

      const validLVTransmuralityViewIDs = [InitViewIDs.ContoursLeft, InitViewIDs.ContoursRight];

      const viewportIds = Object.keys(this.viewportStates);
      viewportIds.forEach((viewportId) => {
        this.setSaxLvTransmuralityOffsetEndo(viewportId, endoOffset);
        this.setSaxLvTransmuralityOffsetEpi(viewportId, epiOffset);

        // Set SAXLvTransmuralityOffsets to active for needed viewports:
        if (validLVTransmuralityViewIDs.includes(viewportId)) {
          this.setVisibleGeneratedContourIds(viewportId, [{ label: ContourLabel.SaxLvTransmurality, id: encodeContourLabelItemId([endoOffset]) }, { label: ContourLabel.SaxLvTransmurality, id: encodeContourLabelItemId([100 - epiOffset]) }]);
        }
      });
    },
    getSaxLvTransmuralityOffsetEndo(viewportId: string): number {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].saxLvTransmuralityOffsetEndo;
    },
    setSaxLvTransmuralityOffsetEpi(viewportId: string, offset: number) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].saxLvTransmuralityOffsetEpi = offset;
    },
    getSaxLvTransmuralityOffsetEpi(viewportId: string): number {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].saxLvTransmuralityOffsetEpi;
    },

    setVisibleGeneratedContours(
      viewportId: string,
      contours: ContourDefinitions.ContourLabel[],
    ) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].visibleGeneratedContours = contours;
    },
    getVisibleGeneratedContours(
      viewportId: string,
    ): ContourDefinitions.ContourLabel[] {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].visibleGeneratedContours;
    },

    setVisibleGeneratedContourIds(
      viewportId: string,
      contourIds: ContourDefinitions.ContourId[],
    ) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].visibleGeneratedContourIds = contourIds;
    },
    getVisibleGeneratedContourIds(
      viewportId: string,
    ): ContourDefinitions.ContourId[] {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].visibleGeneratedContourIds;
    },

    setHiddenUserEditableContours(
      viewportId: string,
      contours: ContourDefinitions.ContourLabel[],
    ) {
      this.initViewportState(viewportId);
      this.viewportStates[viewportId].hiddenUserEditableContours = contours;
    },

    getHiddenUserEditableContours(
      viewportId: string,
    ): ContourDefinitions.ContourLabel[] {
      this.initViewportState(viewportId);
      return this.viewportStates[viewportId].hiddenUserEditableContours;
    },

    getCompatibleImageIndex(
      viewportId: string,
      imageIndices: ImageIndices,
      currentImageIndices: ImageIndices,
    ): ImageIndices {
      // Check if the viewport is restricted
      if (!RestrictToBreathholdViewIDs.includes(viewportId)) {
        return imageIndices;
      }

      // If viewport is Analysis, we want to restric to specific image indices IF
      // user wants to dsiplay static masks:
      // For now keep it simple, if static then restrict phase changes assuming that
      // the viewer is updated to correct image index when user changes to static mask.
      const isStaticMaskSelected
        = this.getSelectedMaskType(viewportId) === 'Static';
      if (isStaticMaskSelected) {
        // Check if imageIdx is changing:
        if (imageIndices.imageIdx !== currentImageIndices.imageIdx) {
          useAlert().warning(
            `Changing phase not allowed in ${viewportId} when Static mask is selected.`,
          );
          return currentImageIndices;
        }
      }

      // If restricted, ensure the image index is within the breathhold range
      const activeStore = useActiveStudyStore();
      const breathholdState = activeStore.getBreathholdState(
        imageIndices.seriesIdx.toString(),
        imageIndices.sliceIdx,
      );

      const start = breathholdState?.getStart() ?? null;
      const end = breathholdState?.getEnd() ?? null;
      // If start/end are not set return imageIndices:
      if (start === null || end === null) {
        return imageIndices;
      }

      const newIdx = imageIndices.imageIdx;
      const currentIdx = currentImageIndices.imageIdx;

      // If within range, use the new index
      if (newIdx >= start && newIdx <= end) {
        return imageIndices;
      }

      // Out of range - clamp to nearest boundary based on whether coming from index 0
      const localIndices = { ...imageIndices };
      if (newIdx > end) {
        localIndices.imageIdx = currentIdx === 0 ? end : start;
      }
      else {
        // newIdx < start
        localIndices.imageIdx = currentIdx === start ? end : start;
      }

      return localIndices;
    },

    /**
     * Find the next series index with a compatible orientation for the viewport.
     * Returns the index or null if none found.
     * If nextRequestedIndex is provided and less than startIndex, search backwards (circular).
     * Otherwise, search forwards (circular).
     */
    getNextCompatibleSeries(
      viewportId: string,
      startIndex: number,
      nextRequestedIndex?: number,
    ): number | null {
      const currentStudyStore = useCurrentStudyStore();
      const totalSeries = currentStudyStore.getSerieCount();
      if (totalSeries === 0) return null;

      const normalize = (idx: number) =>
        ((idx % totalSeries) + totalSeries) % totalSeries;

      const indices: number[] = [];

      if (
        typeof nextRequestedIndex === 'number'
        && nextRequestedIndex !== startIndex
      ) {
        // Determine direction: backward if nextRequestedIndex < startIndex, else forward
        const direction = nextRequestedIndex < startIndex ? -1 : 1;
        let idx = normalize(nextRequestedIndex);
        // Collect all indices in circular order starting from nextRequestedIndex, wrapping around, ending at startIndex
        while (idx !== startIndex) {
          indices.push(idx);
          idx = normalize(idx + direction);
        }
        indices.push(startIndex); // include startIndex at the end
      }
      else {
        // Default: forward circular search starting from startIndex+1
        let idx = normalize(startIndex + 1);
        while (idx !== startIndex) {
          indices.push(idx);
          idx = normalize(idx + 1);
        }
        indices.push(startIndex); // include startIndex at the end
      }

      for (const i of indices) {
        const classification
          = seriesClassification.getClassificationByViewport(viewportId);

        if (!classification) {
          return i;
        }
        const orientationSupported = this.isOrientationSupportedByViewID(
          viewportId,
          classification.orientation,
        );
        if (orientationSupported) {
          return i;
        }
      }
      return null;
    },
  },
});
